import React, { useState, useEffect, useRef } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "./Button";
import Input from "./Input";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";

/**
 * Search-and-pick one entity (event / artist / venue / …) by name or exact ID.
 *
 * The admin list endpoints all match an entity's own ID as well as its name, so one
 * input covers both. Two-character minimum, debounced through the shared
 * `useDebouncedValue` hook, superseded requests aborted.
 *
 * Once something is picked the search box is *replaced* by a confirmed chip: a
 * selection that isn't on screen can never be submitted by mistake.
 *
 * Props:
 *   search(query, axiosConfig) -> Promise<item[]>  — pass the config through so the
 *                                                    abort signal reaches axios
 *   optionLabel/optionSublabel(item)  — row text
 *   value / onChange                  — `{ id, label }` or null; `label: null` means
 *                                       the admin pasted an ID we never resolved
 *   allowRawId                        — offer "use this as an ID" when nothing matched
 *   excludeId                         — id to drop from results (e.g. the event you're
 *                                       already looking at)
 */
export function EntitySearchPicker({
  id,
  label,
  required = false,
  placeholder = "Search by name or paste an ID...",
  search,
  optionLabel,
  optionSublabel,
  value = null,
  onChange,
  disabled = false,
  allowRawId = true,
  emptyLabel = "No match for that name or ID.",
  excludeId = null,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debouncedQuery = useDebouncedValue(query.trim(), 350);
  // Held in a ref so an inline `search={(q) => ...}` at the call site doesn't make the
  // effect re-run (and re-fetch) on every render.
  const searchRef = useRef(search);
  searchRef.current = search;

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    (async () => {
      try {
        setResults(await searchRef.current(debouncedQuery, { signal: controller.signal }));
      } catch {
        // Includes the abort of a superseded query — the next one owns the state.
        if (!controller.signal.aborted) setResults([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [debouncedQuery]);

  const handleSelect = (picked) => {
    setQuery("");
    setResults([]);
    onChange(picked);
  };

  if (value) {
    return (
      <div>
        {label && <p className="mb-1 block text-sm font-medium">{label}</p>}
        <div className="flex items-center justify-between gap-2 rounded-md border border-input bg-background p-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{value.label || "Unverified ID"}</p>
            <p className="truncate font-mono text-xs text-muted-foreground">{value.id}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onChange(null)} disabled={disabled}>
            <X className="mr-1 h-4 w-4" />
            Change
          </Button>
        </div>
      </div>
    );
  }

  const trimmed = query.trim();
  const visible = excludeId ? results.filter((item) => item.id !== excludeId) : results;

  return (
    <div className="relative">
      <Input
        id={id}
        label={label}
        required={required}
        placeholder={placeholder}
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
      />
      {trimmed.length >= 2 && (
        <div className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded-md border border-border bg-background shadow-lg">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Searching...
            </div>
          )}
          {!loading &&
            visible.map((item) => {
              const itemLabel = optionLabel(item);
              const sublabel = optionSublabel?.(item);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleSelect({ id: item.id, label: itemLabel })}
                  className="block w-full text-left px-3 py-2 text-sm hover:bg-accent"
                >
                  <span className="font-medium">{itemLabel}</span>
                  {sublabel ? <span className="text-muted-foreground"> · {sublabel}</span> : null}
                  <span className="block font-mono text-xs text-muted-foreground">{item.id}</span>
                </button>
              );
            })}
          {/* Search matches an entity's own exact ID, so a pasted ID normally resolves to
              its row above. `allowRawId` only helps for an entity the admin list can't
              reach; the API's 404 is the safety net behind it. Note this branch is keyed
              on the *visible* rows, so filtering out `excludeId` still leaves feedback
              instead of an unexplained blank dropdown. */}
          {!loading &&
            visible.length === 0 &&
            (allowRawId ? (
              <button
                type="button"
                onClick={() => handleSelect({ id: trimmed, label: null })}
                className="block w-full text-left px-3 py-2 text-sm hover:bg-accent"
              >
                No matches — use “{trimmed}” as an entity ID
              </button>
            ) : (
              <p className="px-3 py-2 text-sm text-muted-foreground">{emptyLabel}</p>
            ))}
        </div>
      )}
    </div>
  );
}

export default EntitySearchPicker;
